import { Building, ChevronLeft, ChevronRight, CircleUser, Trash2, UserPlus, Users } from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageLayout } from '@/components/Layout/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { ConfirmationModal } from '@/components/Common/ConfirmationModal';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/Common/accordion';
import { useIsSuperAdmin, useAuthStore } from '@/stores/authStore';
import adminUsersService from '@/services/adminUsersService';
import userService from '@/services/userService';
import { createInvitation } from '@/services/invitationService';
import {
  AdminUserStats,
  UserWithLogtoStatus,
  NonSyncResponse,
  OrganizationWithUsers,
  User,
  DeletedUser,
  Organization,
  AdminUserRow,
  LogtoUserStub,
  UserRole,
  UserRoles,
} from '@/types';
import {
  DEFAULT_PROFILE_FILTER,
  isCompleteDateInput,
  type AdminUserFilters,
  type AdminUserSort,
  type AdminUserSortField,
} from '@/types/adminUsers';
import { InviteUserModal, InviteUserFormData } from '@/components/Admin/InviteUserModal';
import { AdminUsersTable } from '@/components/Admin/AdminUsersTable';
import { AdminUsersFilters } from '@/components/Admin/AdminUsersFilters';
import { useProfiles } from '@/hooks/useProfiles';
import profileService from '@/services/profileService';
import SafeDeleteUserDialog from '@/components/Admin/SafeDeleteUserDialog';
import DeletionLogModal from '@/components/Admin/DeletionLogModal';
import { notify } from '@/lib/notify';

type TabId = 'users' | 'non-sync' | 'orgas' | 'deleted' | 'login-as' | 'demo';

const EMPTY_USERS_FILTERS: AdminUserFilters = {
  organizationId: '',
  profileId: '',
  role: '',
  createdFrom: '',
  createdTo: '',
};

const DEFAULT_USERS_SORT: AdminUserSort = { field: 'created_at', order: 'desc' };

/**
 * Local midnight of a YYYY-MM-DD date (plus dayOffset days) as an ISO string,
 * so the created-date range follows the admin's timezone rather than UTC.
 * `new Date('YYYY-MM-DD')` would parse as UTC midnight, hence the manual split.
 */
function localDayStartIso(value: string, dayOffset = 0): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match || !isCompleteDateInput(value)) return undefined;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + dayOffset);
  return isNaN(date.getTime()) ? undefined : date.toISOString();
}

function toRowFromUserWithStatus(u: UserWithLogtoStatus): AdminUserRow {
  return {
    id: u.id,
    email: u.email,
    name: u.name ?? null,
    created_at: u.created_at ?? null,
    last_sign_in_at: u.last_sign_in_at ?? null,
    logto_status: u.logto_status,
    organization_name: u.organization_name ?? null,
    organization_id: u.organization_id ?? null,
    logto_id: u.logto_id ?? null,
    source: u.logto_status === 'active' ? 'db_and_logto' : 'db',
    is_active: u.is_active,
    role: u.role ?? UserRoles.User,
    profile_id: u.profile_id ?? null,
    profile_key: u.profile?.key ?? null,
    profile_name: u.profile?.name ?? null,
  };
}

function toRowFromDbUser(u: User): AdminUserRow {
  return {
    id: u.id,
    email: u.email,
    name: u.name ?? null,
    created_at: u.created_at ?? null,
    last_sign_in_at: null,
    logto_status: 'not_in_logto',
    organization_name: u.organization?.name ?? null,
    organization_id: u.organization_id ?? null,
    logto_id: u.logto_id ?? null,
    source: 'db',
    is_active: u.is_active,
    role: u.role,
    profile_id: u.profile_id ?? null,
    profile_key: u.profile?.key ?? null,
    profile_name: u.profile?.name ?? null,
  };
}

function toRowFromLogtoStub(lu: LogtoUserStub): AdminUserRow {
  return {
    id: lu.id,
    email: lu.primary_email ?? lu.id,
    name: lu.name ?? null,
    created_at: null,
    last_sign_in_at: null,
    logto_status: undefined,
    organization_name: null,
    organization_id: null,
    logto_id: lu.id,
    source: 'logto',
  };
}

function toRowFromOrgMember(member: User, organisationName?: string): AdminUserRow {
  return {
    id: member.id,
    email: member.email,
    name: member.name ?? null,
    created_at: member.created_at ?? null,
    last_sign_in_at: null,
    logto_status: member.logto_id ? 'active' : 'not_in_logto',
    organization_name: member.organization?.name ?? organisationName ?? null,
    organization_id: member.organization_id ?? null,
    logto_id: member.logto_id ?? null,
    source: member.logto_id ? 'db_and_logto' : 'db',
    is_active: member.is_active,
    role: member.role ?? UserRoles.User,
    profile_id: member.profile_id ?? null,
    profile_key: member.profile?.key ?? null,
    profile_name: member.profile?.name ?? null,
  };
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();

  const [stats, setStats] = useState<AdminUserStats | null>(null);
  const [users, setUsers] = useState<UserWithLogtoStatus[]>([]);
  const [nonSync, setNonSync] = useState<NonSyncResponse | null>(null);
  const [orgsWithMembers, setOrgsWithMembers] = useState<OrganizationWithUsers[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('users');
  const [error, setError] = useState<string | null>(null);

  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(20);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersFilters, setUsersFilters] = useState<AdminUserFilters>(EMPTY_USERS_FILTERS);
  const [usersSort, setUsersSort] = useState<AdminUserSort>(DEFAULT_USERS_SORT);
  // Only the latest list request may write state, so a slow response can't overwrite newer filters.
  const usersRequestIdRef = useRef(0);
  // Own flag for the Users tab: the shared `loading` is cleared by loadStats, which used to
  // finish first and flash "No users" before the list arrived.
  const [usersLoading, setUsersLoading] = useState(true);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitePrefillEmail, setInvitePrefillEmail] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<AdminUserRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [assigningUserId, setAssigningUserId] = useState<string | null>(null);

  // Safe-delete state
  const [safeDeleteUser, setSafeDeleteUser] = useState<User | null>(null);
  const [deletedUsers, setDeletedUsers] = useState<DeletedUser[]>([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [deletedError, setDeletedError] = useState<string | null>(null);
  const [selectedDeletedUser, setSelectedDeletedUser] = useState<DeletedUser | null>(null);

  // Login-as tab state
  const [loginAsUsers, setLoginAsUsers] = useState<User[]>([]);
  const [loginAsTotal, setLoginAsTotal] = useState(0);
  const [loginAsLoading, setLoginAsLoading] = useState(false);
  const [connectingId, setConnectingId] = useState<string | null>(null);

  // Demo tab state
  const [demoUsers, setDemoUsers] = useState<UserWithLogtoStatus[]>([]);
  const [demoTotal, setDemoTotal] = useState(0);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [selectedDemoIds, setSelectedDemoIds] = useState<Set<string>>(new Set());
  const [showDeleteAllDemoDialog, setShowDeleteAllDemoDialog] = useState(false);
  const [showDeleteSelectedDemoDialog, setShowDeleteSelectedDemoDialog] = useState(false);
  const [deletingDemo, setDeletingDemo] = useState(false);

  // Logto admin console URL for "Show in Logto" link (console/users/{id}/settings). Fallback to tenant endpoint if same host.
  const logtoDashboardUrl =
    (typeof import.meta !== 'undefined' && ((import.meta as any).env?.VITE_LOGTO_CONSOLE_URL ?? (import.meta as any).env?.VITE_LOGTO_ENDPOINT)) ||
    null;

  useEffect(() => {
    if (isSuperAdmin) {
      loadStats();
      loadOrganizations();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    if (activeTab === 'non-sync') loadNonSync();
    else if (activeTab === 'orgas') loadOrgsWithMembers();
    else if (activeTab === 'deleted') loadDeletedUsers();
    else if (activeTab === 'login-as') loadLoginAsUsers();
    else if (activeTab === 'demo') loadDemoUsers();
  }, [isSuperAdmin, activeTab]);

  useEffect(() => {
    if (!isSuperAdmin || activeTab !== 'users') return;
    loadUsers();
  }, [isSuperAdmin, activeTab, usersPage, usersPageSize, usersFilters, usersSort]);

  const loadStats = async () => {
    try {
      const data = await adminUsersService.getStats();
      setStats(data);
    } catch {
      setError(t('admin.users.statsError', 'Failed to load stats'));
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const data = await import('@/services/organizationService').then((m) => m.default.getOrganizations());
      setOrganizations(data ?? []);
    } catch (e) {
      console.error('Load organizations failed', e);
    }
  };

  const loadUsers = async () => {
    const requestId = ++usersRequestIdRef.current;
    try {
      setUsersLoading(true);
      const skip = (usersPage - 1) * usersPageSize;
      const data = await adminUsersService.getList({
        skip,
        limit: usersPageSize,
        sort_by: usersSort.field,
        sort_order: usersSort.order,
        organization_id: usersFilters.organizationId || undefined,
        profile_id:
          usersFilters.profileId && usersFilters.profileId !== DEFAULT_PROFILE_FILTER ? usersFilters.profileId : undefined,
        without_profile: usersFilters.profileId === DEFAULT_PROFILE_FILTER || undefined,
        role: usersFilters.role || undefined,
        created_from: localDayStartIso(usersFilters.createdFrom),
        // "to" is inclusive in the UI; the API bound is exclusive, so send the next local midnight.
        created_before: localDayStartIso(usersFilters.createdTo, 1),
      });
      if (requestId !== usersRequestIdRef.current) return;
      setUsers(data?.items ?? []);
      setUsersTotal(data?.total ?? 0);
      // Clear only our own earlier failure; other banners (stats, mutations) stay.
      setError((prev) => (prev === t('admin.users.listError', 'Failed to load users') ? null : prev));
    } catch {
      if (requestId !== usersRequestIdRef.current) return;
      setError(t('admin.users.listError', 'Failed to load users'));
    } finally {
      if (requestId === usersRequestIdRef.current) setUsersLoading(false);
    }
  };

  // Filter/sort and page are set in the same handler so React batches them into a single fetch.
  const handleUsersFiltersChange = (next: AdminUserFilters) => {
    setUsersFilters(next);
    setUsersPage(1);
  };

  const handleUsersFiltersReset = () => {
    setUsersFilters(EMPTY_USERS_FILTERS);
    setUsersPage(1);
  };

  const handleUsersSortChange = (field: AdminUserSortField) => {
    setUsersSort((prev) =>
      prev.field === field
        ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: field === 'created_at' ? 'desc' : 'asc' },
    );
    setUsersPage(1);
  };

  const hasActiveUsersFilters = Object.values(usersFilters).some((v) => v !== '');

  const loadNonSync = async () => {
    try {
      setLoading(true);
      const data = await adminUsersService.getNonSync();
      setNonSync(data);
    } catch {
      setError(t('admin.users.nonSyncError', 'Failed to load non-sync data'));
      setNonSync(null);
    } finally {
      setLoading(false);
    }
  };

  const loadOrgsWithMembers = async () => {
    try {
      setLoading(true);
      const data = await adminUsersService.getOrganizationsWithMembers();
      setOrgsWithMembers(data);
    } catch {
      setError(t('admin.users.orgsError', 'Failed to load organizations'));
      setOrgsWithMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedUsers = async () => {
    setLoadingDeleted(true);
    setDeletedError(null);
    try {
      const data = await userService.getDeletedUsers();
      setDeletedUsers(data);
    } catch {
      setDeletedError(t('admin.users.deletedUsersTab.loadError'));
    } finally {
      setLoadingDeleted(false);
    }
  };

  const loadLoginAsUsers = async () => {
    setLoginAsLoading(true);
    try {
      const data = await adminUsersService.getList({ skip: 0, limit: 200 });
      setLoginAsUsers(data?.items ?? []);
      setLoginAsTotal(data?.total ?? 0);
    } catch {
      setError(t('admin.loginAs.loading', 'Failed to load users'));
    } finally {
      setLoginAsLoading(false);
    }
  };

  const loadDemoUsers = useCallback(async () => {
    setLoadingDemo(true);
    setDemoError(null);
    try {
      const data = await adminUsersService.getDemoUsers({ skip: 0, limit: 200 });
      setDemoUsers(data?.items ?? []);
      setDemoTotal(data?.total ?? 0);
      setSelectedDemoIds(new Set());
    } catch {
      setDemoError(t('admin.users.demoTab.loadError', 'Failed to load demo users'));
    } finally {
      setLoadingDemo(false);
    }
  }, [t]);

  const handleConnectAs = (user: User) => {
    setConnectingId(user.id);
    const { startImpersonation } = useAuthStore.getState().actions;
    startImpersonation(user);
    setConnectingId(null);
    navigate('/welcome', { replace: true });
  };

  const handleInvite = async (data: InviteUserFormData) => {
    await createInvitation(data);
    notify.show({
      variant: 'success',
      title: t('admin.users.invite.success', 'Invitation sent'),
      description: data.email ? `An invitation email has been sent to ${data.email}.` : undefined,
    });
    setInvitePrefillEmail(null);
    loadUsers();
    if (activeTab === 'non-sync') loadNonSync();
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      // Logto-only users: hard delete from Logto
      if (userToDelete.source === 'logto') {
        await adminUsersService.deleteLogtoUser(userToDelete.id);
        notify.show({
          variant: 'success',
          title: t('admin.users.deleteFromLogtoSuccess', 'User deleted from Logto'),
          description: userToDelete.email ? `${userToDelete.email} has been removed from Logto.` : undefined,
        });
        if (activeTab === 'non-sync') loadNonSync();
      }
      setUserToDelete(null);
    } catch (e: any) {
      notify.error(e?.response?.data?.detail || t('admin.users.deleteError', 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  // Called when the safe-delete dialog triggers delete for a DB user
  const handleSafeDeleteRow = (row: AdminUserRow) => {
    // Build a minimal User object for the dialog
    const userObj: User = {
      id: row.id,
      name: row.name || row.email,
      email: row.email,
      role: (row.role as UserRole) || UserRoles.User,
      organization_id: row.organization_id ?? null,
      is_active: row.is_active,
      logto_id: row.logto_id,
    };
    setSafeDeleteUser(userObj);
  };

  const handleSafeDeleteSuccess = () => {
    notify.show({
      variant: 'success',
      title: t('admin.users.safeDelete.success'),
    });
    loadUsers();
    loadStats();
    if (activeTab === 'deleted') loadDeletedUsers();
  };

  const { profiles: allProfiles } = useProfiles();

  const handleAssignProfile = async (userId: string, profileId: string | null) => {
    try {
      await profileService.assignToUser(userId, profileId);
      notify.success(t('admin.users.profileAssigned', 'Profile updated'));
      loadUsers();
    } catch (e: any) {
      notify.error(e?.response?.data?.detail || t('admin.users.profileAssignError', 'Failed to assign profile'));
    }
  };

  const handleAssignOrga = async (userId: string, organizationId: string | null) => {
    setAssigningUserId(userId);
    try {
      await userService.updateUserOrganization(userId, organizationId || null);
      notify.show({
        variant: 'success',
        title: t('admin.users.assignSuccess', 'User assigned'),
        description: organizationId
          ? 'The user has been assigned to the organization.'
          : 'The user has been unassigned from their organization.',
      });
      loadUsers();
      if (activeTab === 'orgas') loadOrgsWithMembers();
    } catch (e: any) {
      notify.error(e?.response?.data?.detail || t('admin.users.assignError', 'Assign failed'));
    } finally {
      setAssigningUserId(null);
    }
  };

  const handleResendMagicLink = async (row: AdminUserRow) => {
    try {
      const res = await userService.resendMagicLink(row.id);
      if (res.sent) {
        notify.show({
          variant: 'success',
          title: t('admin.users.resendSuccess', 'Password reset email sent'),
          description: res.email ? `A password reset link has been sent to ${res.email}.` : undefined,
        });
      } else {
        notify.info(res.message || t('admin.users.resendInfo', 'See message'));
      }
    } catch (e: any) {
      notify.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const handleAddToDb = async (row: AdminUserRow, organizationId?: string | null) => {
    try {
      await adminUsersService.addUserFromLogto(row.id, organizationId);
      notify.show({
        variant: 'success',
        title: t('admin.users.addToDbSuccess', 'User added to database'),
        description: row.email ? `${row.email} is now synchronized.` : undefined,
        link: '/admin/users',
      });
      loadUsers();
      loadNonSync();
      loadStats();
    } catch (e: any) {
      notify.error(e?.response?.data?.detail || t('admin.users.addToDbError', 'Add to DB failed'));
    }
  };

  const handleDeleteFromLogto = (row: AdminUserRow) => {
    setUserToDelete(row);
  };

  const handleInviteRow = (row: AdminUserRow) => {
    setInvitePrefillEmail(row.email);
    setShowInviteModal(true);
  };

  const handleToggleActive = async (row: AdminUserRow, isActive: boolean) => {
    try {
      await userService.toggleUserActive(row.id, isActive);
      notify.show({
        variant: 'success',
        title: isActive
          ? t('admin.users.activateSuccess', 'User activated')
          : t('admin.users.deactivateSuccess', 'User deactivated'),
        description: row.email
          ? `${row.email} has been ${isActive ? 'activated' : 'deactivated'}.`
          : undefined,
      });
      loadUsers();
    } catch (e: any) {
      notify.error(e?.response?.data?.detail || t('admin.users.toggleActiveError', 'Operation failed'));
    }
  };

  const handleRoleChange = async (row: AdminUserRow, newRole: UserRole) => {
    try {
      await userService.updateUserRole(row.id, newRole);
      notify.show({
        variant: 'success',
        title: t('admin.users.roleUpdated', 'Role updated'),
        description: row.email ? `${row.email} → ${newRole}` : undefined,
      });
      loadUsers();
    } catch (e: any) {
      notify.error(e?.response?.data?.detail || t('admin.users.roleUpdateError', 'Failed to update role'));
    }
  };

  // Demo tab handlers
  const toggleDemoSelection = (id: string) => {
    setSelectedDemoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllDemoSelection = () => {
    if (selectedDemoIds.size === demoUsers.length) {
      setSelectedDemoIds(new Set());
    } else {
      setSelectedDemoIds(new Set(demoUsers.map((u) => u.id)));
    }
  };

  const handleDeleteDemoUsers = async (ids: string[]) => {
    setDeletingDemo(true);
    let successCount = 0;
    let errorCount = 0;
    for (const id of ids) {
      try {
        await userService.deleteUser(id);
        successCount++;
      } catch {
        errorCount++;
      }
    }
    setDeletingDemo(false);
    setShowDeleteAllDemoDialog(false);
    setShowDeleteSelectedDemoDialog(false);
    if (successCount > 0) {
      notify.show({
        variant: 'success',
        title: t('admin.users.demoTab.deleteSuccess', 'Demo users deleted'),
        description: `${successCount} user(s) deleted.`,
      });
    }
    if (errorCount > 0) {
      notify.error(t('admin.users.demoTab.deleteError', 'Some deletions failed') + ` (${errorCount} errors)`);
    }
    loadDemoUsers();
    loadStats();
  };

  if (!isSuperAdmin) {
    return (
      <div className="w-full flex items-center justify-center py-16">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              {t('admin.users.unauthorizedTitle', 'Unauthorized')}
            </CardTitle>
            <CardDescription>
              {t('admin.users.unauthorizedDescription', 'Only super-administrators can access this page.')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const tabs: { id: TabId; labelKey: string; label: string }[] = [
    { id: 'users', labelKey: 'admin.users.tabs.users', label: 'Users' },
    { id: 'non-sync', labelKey: 'admin.users.tabs.nonSync', label: 'Non-Sync' },
    { id: 'orgas', labelKey: 'admin.users.tabs.organisations', label: 'Organisations' },
    { id: 'deleted', labelKey: 'admin.users.tabs.deletedUsers', label: 'Deleted Users' },
    { id: 'login-as', labelKey: 'admin.users.tabs.loginAs', label: 'Login As' },
    { id: 'demo', labelKey: 'admin.users.tabs.demo', label: 'Demo' },
  ];

  return (
    <PageLayout
      icon={<Users className="h-6 w-6" />}
      title={t('admin.users.title', 'Users')}
      subtitle={t('admin.users.subtitle', 'Manage platform users (Logto & DB) and organizations')}
      headerRight={
        <Button onClick={() => setShowInviteModal(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          {t('admin.users.inviteUser', 'Invite user')}
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('admin.users.stats.totalUsers', 'Total users')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-2xl font-semibold">{stats.total_users}</span>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('admin.users.stats.totalOrgs', 'Organizations')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-2xl font-semibold">{stats.total_organizations}</span>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('admin.users.stats.inLogto', 'In Logto')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-2xl font-semibold text-green-600">{stats.users_in_logto}</span>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('admin.users.stats.notInLogto', 'DB only')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-2xl font-semibold text-amber-600">{stats.users_not_in_logto}</span>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('admin.users.stats.logtoOnly', 'Logto only')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <span className="text-2xl font-semibold text-blue-600">{stats.logto_not_in_db}</span>
              </CardContent>
            </Card>
          </div>
        )}

        {error && (
          <Card className="border-destructive/40">
            <CardContent className="py-3 text-destructive">{error}</CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(tab.labelKey, tab.label)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'users' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.users.tabs.users', 'Users')}</CardTitle>
              <CardDescription>
                {t('admin.users.usersDescription', 'DB users with Logto status and assigned organization.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AdminUsersFilters
                value={usersFilters}
                onChange={handleUsersFiltersChange}
                organizations={organizations}
                profiles={allProfiles}
                onReset={handleUsersFiltersReset}
              />
              {/* Keep rows mounted while refetching so a sort/filter click doesn't drop keyboard focus. */}
              <div className="relative" aria-busy={usersLoading}>
                {usersLoading && users.length > 0 && (
                  <div className="absolute inset-0 z-10 flex items-start justify-center bg-background/60 pt-12">
                    <div
                      role="status"
                      aria-live="polite"
                      className="flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm text-muted-foreground shadow-sm"
                    >
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      {t('admin.users.loadingUsers', 'Loading users…')}
                    </div>
                  </div>
                )}
                <AdminUsersTable
                  rows={users.map(toRowFromUserWithStatus)}
                  organizations={organizations}
                  loading={usersLoading && users.length === 0}
                  assigningUserId={assigningUserId}
                  showCreated
                  showLogtoStatus
                  showOrganization
                  showRole
                  showProfile={isSuperAdmin}
                  profiles={allProfiles}
                  onAssignProfile={isSuperAdmin ? handleAssignProfile : undefined}
                  logtoDashboardUrl={logtoDashboardUrl}
                  onAssignOrga={handleAssignOrga}
                  onResendMagicLink={handleResendMagicLink}
                  onDelete={(row) => {
                    if (row.source === 'logto') {
                      setUserToDelete(row);
                    } else {
                      handleSafeDeleteRow(row);
                    }
                  }}
                  onToggleActive={handleToggleActive}
                  onRoleChange={handleRoleChange}
                  sort={usersSort}
                  onSortChange={handleUsersSortChange}
                  emptyMessage={
                    hasActiveUsersFilters
                      ? t('admin.users.filters.noMatch', 'No users match these filters')
                      : t('admin.users.noUsers', 'No users')
                  }
                />
              </div>
              {!usersLoading && usersTotal > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      {t('admin.users.pagination.showing', 'Showing {{from}}–{{to}} of {{total}}', {
                        from: (usersPage - 1) * usersPageSize + 1,
                        to: Math.min(usersPage * usersPageSize, usersTotal),
                        total: usersTotal,
                      })}
                    </span>
                    <select
                      className="rounded border border-input bg-background px-2 py-1 text-sm"
                      value={usersPageSize}
                      onChange={(e) => {
                        setUsersPageSize(Number(e.target.value));
                        setUsersPage(1);
                      }}
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n} {t('admin.users.pagination.perPage', 'per page')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                      disabled={usersPage <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      {t('admin.users.pagination.page', 'Page {{current}} of {{total}}', {
                        current: usersPage,
                        total: Math.max(1, Math.ceil(usersTotal / usersPageSize)),
                      })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsersPage((p) => p + 1)}
                      disabled={usersPage >= Math.ceil(usersTotal / usersPageSize)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'non-sync' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.users.tabs.nonSync', 'Non-Sync')}</CardTitle>
              <CardDescription>
                {t('admin.users.nonSyncDescription', 'Users in DB but not in Logto, and users in Logto but not in DB.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div>
                <h4 className="font-medium mb-3">
                  {t('admin.users.inDbNotLogto', 'In DB, not in Logto')} ({nonSync?.in_db_not_in_logto?.length ?? 0})
                </h4>
                <AdminUsersTable
                  rows={(nonSync?.in_db_not_in_logto ?? []).map(toRowFromDbUser)}
                  organizations={organizations}
                  loading={loading}
                  assigningUserId={assigningUserId}
                  showCreated
                  showLogtoStatus={false}
                  showOrganization
                  onAssignOrga={handleAssignOrga}
                  onDelete={(row) => {
                    if (row.source === 'logto') {
                      setUserToDelete(row);
                    } else {
                      handleSafeDeleteRow(row);
                    }
                  }}
                  onInvite={handleInviteRow}
                  emptyMessage={t('admin.users.none', 'None')}
                />
              </div>
              <div>
                <h4 className="font-medium mb-3">
                  {t('admin.users.inLogtoNotDb', 'In Logto, not in DB')} ({nonSync?.in_logto_not_in_db?.length ?? 0})
                </h4>
                <AdminUsersTable
                  rows={(nonSync?.in_logto_not_in_db ?? []).map(toRowFromLogtoStub)}
                  organizations={organizations}
                  loading={loading}
                  showCreated={false}
                  showLogtoStatus={false}
                  showOrganization={false}
                  onAddToDb={handleAddToDb}
                  onDeleteFromLogto={handleDeleteFromLogto}
                  emptyMessage={t('admin.users.none', 'None')}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'orgas' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.users.tabs.organisations', 'Organisations')}</CardTitle>
              <CardDescription>
                {t('admin.users.orgsDescription', 'Organizations with their members.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {(orgsWithMembers ?? []).map((org) => (
                    <AccordionItem key={org.id} value={org.id}>
                      <AccordionTrigger>
                        <span className="flex items-center gap-2">
                          <Building className="h-5 w-5 text-muted-foreground" />
                          {org.name}
                          {org.domain && (
                            <span className="text-muted-foreground font-normal">({org.domain})</span>
                          )}
                          <span className="text-muted-foreground text-sm font-normal ml-2">
                            {(org.users?.length ?? 0)} {t('admin.users.members', 'members')}
                          </span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2">
                          {org.description && (
                            <p className="text-sm text-muted-foreground">{org.description}</p>
                          )}
                          <AdminUsersTable
                            rows={(org.users ?? []).map((m: User) => toRowFromOrgMember(m, org.name))}
                            organizations={organizations}
                            showCreated
                            showLogtoStatus
                            showOrganization
                            logtoDashboardUrl={logtoDashboardUrl}
                            onAssignOrga={handleAssignOrga}
                            onResendMagicLink={handleResendMagicLink}
                            onDelete={(row) => {
                              if (row.source === 'logto') {
                                setUserToDelete(row);
                              } else {
                                handleSafeDeleteRow(row);
                              }
                            }}
                            assigningUserId={assigningUserId}
                            emptyMessage={t('admin.users.noMembers', 'No members')}
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
              {!loading && orgsWithMembers.length === 0 && (
                <p className="text-muted-foreground text-center py-6">{t('admin.users.noOrgs', 'No organizations')}</p>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'deleted' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.users.tabs.deletedUsers')}</CardTitle>
              <CardDescription>
                {t('admin.users.deletedUsersTab.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingDeleted ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : deletedError ? (
                <p className="text-destructive text-sm">{deletedError}</p>
              ) : deletedUsers.length === 0 ? (
                <p className="text-muted-foreground text-center py-6">
                  {t('admin.users.deletedUsersTab.noDeletedUsers')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground text-xs uppercase">
                        <th className="text-left py-2 px-3">{t('admin.users.table.email')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.table.name')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.deletedUsersTab.deletedAt')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.deletedUsersTab.deletedBy')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.deletedUsersTab.transferredTo')}</th>
                        <th className="text-right py-2 px-3">{t('admin.users.table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deletedUsers.map((u) => (
                        <tr key={u.id} className="border-b hover:bg-muted/40">
                          <td className="py-2 px-3 text-muted-foreground">{u.email}</td>
                          <td className="py-2 px-3">{u.name || '—'}</td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {u.deleted_at
                              ? new Date(u.deleted_at).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="py-2 px-3">{u.deleted_by_name || '—'}</td>
                          <td className="py-2 px-3">{u.content_transferred_to_name || '—'}</td>
                          <td className="py-2 px-3 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedDeletedUser(u)}
                            >
                              {t('admin.users.deletedUsersTab.viewLog')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'login-as' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CircleUser className="h-5 w-5" />
                {t('admin.loginAs.title')}
              </CardTitle>
              <CardDescription>{t('admin.loginAs.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              {loginAsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : loginAsUsers.length === 0 ? (
                <p className="text-muted-foreground text-center py-6">{t('admin.loginAs.noUsers')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground text-xs uppercase">
                        <th className="text-left py-2 px-3">{t('admin.users.table.name')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.table.email')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.table.organization')}</th>
                        <th className="text-right py-2 px-3">{t('admin.users.table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginAsUsers.map((u) => (
                        <tr key={u.id} className="border-b hover:bg-muted/40">
                          <td className="py-2 px-3">{u.name || '—'}</td>
                          <td className="py-2 px-3 text-muted-foreground">{u.email}</td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {(u as any).organization_name ?? (u.organization as any)?.name ?? '—'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleConnectAs(u)}
                              disabled={!!connectingId}
                            >
                              {connectingId === u.id ? t('admin.loginAs.loading') : t('admin.loginAs.connectAs')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {loginAsTotal > loginAsUsers.length && (
                    <p className="text-muted-foreground text-xs mt-2">
                      {t('admin.users.pagination.showing', 'Showing {{from}}–{{to}} of {{total}}', {
                        from: 1,
                        to: loginAsUsers.length,
                        total: loginAsTotal,
                      })}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'demo' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle>{t('admin.users.tabs.demo', 'Demo')}</CardTitle>
                  <CardDescription className="mt-1">
                    {t('admin.users.demoTab.description', "Demo users (role='demo').")}
                    {demoTotal > 0 && (
                      <span className="ml-2 text-muted-foreground">— {demoTotal} {t('admin.users.stats.totalUsers', 'total')}</span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {selectedDemoIds.size > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteSelectedDemoDialog(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('admin.users.demoTab.deleteSelected', 'Delete selected')} ({selectedDemoIds.size})
                    </Button>
                  )}
                  {demoUsers.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteAllDemoDialog(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('admin.users.demoTab.deleteAll', 'Delete all demo users')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingDemo ? (
                <div className="flex justify-center py-8">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : demoError ? (
                <p className="text-destructive text-sm">{demoError}</p>
              ) : demoUsers.length === 0 ? (
                <p className="text-muted-foreground text-center py-6">
                  {t('admin.users.demoTab.noUsers', 'No demo users.')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground text-xs uppercase">
                        <th className="py-2 px-3 text-left w-8">
                          <input
                            type="checkbox"
                            checked={selectedDemoIds.size === demoUsers.length && demoUsers.length > 0}
                            onChange={toggleAllDemoSelection}
                            aria-label={t('global.selectAll', 'Select all')}
                            className="rounded"
                          />
                        </th>
                        <th className="text-left py-2 px-3">{t('admin.users.table.email', 'Email')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.demoTab.colOrg', 'Organization')}</th>
                        <th className="text-left py-2 px-3">{t('admin.users.demoTab.colCreated', 'Created')}</th>
                        <th className="text-right py-2 px-3">{t('admin.users.table.actions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demoUsers.map((u) => (
                        <tr key={u.id} className="border-b hover:bg-muted/40">
                          <td className="py-2 px-3">
                            <input
                              type="checkbox"
                              checked={selectedDemoIds.has(u.id)}
                              onChange={() => toggleDemoSelection(u.id)}
                              aria-label={`Select ${u.email}`}
                              className="rounded"
                            />
                          </td>
                          <td className="py-2 px-3">{u.email}</td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {u.organization_name ?? '—'}
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {formatDate(u.created_at)}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDeleteDemoUsers([u.id])}
                              disabled={deletingDemo}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <InviteUserModal
        open={showInviteModal}
        onClose={() => { setShowInviteModal(false); setInvitePrefillEmail(null); }}
        onSubmit={handleInvite}
        initialEmail={invitePrefillEmail}
      />

      {/* Logto-only hard delete confirmation */}
      <ConfirmationModal
        open={!!userToDelete}
        title={t('admin.users.deleteFromLogtoTitle', 'Delete from Logto')}
        message={t('admin.users.deleteFromLogtoMessage', 'Remove this user from Logto? They will no longer be able to sign in.')}
        confirmText={t('global.delete')}
        cancelText={t('global.cancel')}
        variant="danger"
        onConfirm={handleDeleteUser}
        onCancel={() => setUserToDelete(null)}
        loading={deleting}
      />

      {/* Safe-delete dialog for DB users */}
      <SafeDeleteUserDialog
        open={!!safeDeleteUser}
        user={safeDeleteUser}
        onClose={() => setSafeDeleteUser(null)}
        onSuccess={handleSafeDeleteSuccess}
      />

      {/* Deletion log + restore modal */}
      <DeletionLogModal
        open={!!selectedDeletedUser}
        user={selectedDeletedUser}
        onClose={() => setSelectedDeletedUser(null)}
        onRestored={() => {
          notify.show({
            variant: 'success',
            title: t('admin.users.restore.success'),
          });
          loadDeletedUsers();
          loadUsers();
          loadStats();
        }}
      />

      {/* Delete all demo users confirmation */}
      <ConfirmationModal
        open={showDeleteAllDemoDialog}
        title={t('admin.users.demoTab.confirmDeleteAllTitle', 'Delete all demo users')}
        message={t('admin.users.demoTab.confirmDeleteAllMessage', 'You are about to permanently delete {{count}} demo user(s). This action cannot be undone.', { count: demoUsers.length })}
        confirmText={t('global.delete', 'Delete')}
        cancelText={t('global.cancel', 'Cancel')}
        variant="danger"
        onConfirm={() => handleDeleteDemoUsers(demoUsers.map((u) => u.id))}
        onCancel={() => setShowDeleteAllDemoDialog(false)}
        loading={deletingDemo}
      >
        <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-muted/40 divide-y divide-border text-xs">
          {demoUsers.map((u) => (
            <li key={u.id} className="px-3 py-1.5 text-foreground truncate">
              {u.name || u.email}
              {u.organization_name && <span className="ml-1.5 text-muted-foreground">— {u.organization_name}</span>}
            </li>
          ))}
        </ul>
      </ConfirmationModal>

      {/* Delete selected demo users confirmation */}
      <ConfirmationModal
        open={showDeleteSelectedDemoDialog}
        title={t('admin.users.demoTab.confirmDeleteSelectedTitle', 'Delete selected demo users')}
        message={t('admin.users.demoTab.confirmDeleteSelectedMessage', 'You are about to permanently delete {{count}} user(s). This action cannot be undone.', { count: selectedDemoIds.size })}
        confirmText={t('global.delete', 'Delete')}
        cancelText={t('global.cancel', 'Cancel')}
        variant="danger"
        onConfirm={() => handleDeleteDemoUsers(Array.from(selectedDemoIds))}
        onCancel={() => setShowDeleteSelectedDemoDialog(false)}
        loading={deletingDemo}
      >
        <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-muted/40 divide-y divide-border text-xs">
          {demoUsers
            .filter((u) => selectedDemoIds.has(u.id))
            .map((u) => (
              <li key={u.id} className="px-3 py-1.5 text-foreground truncate">
                {u.name || u.email}
                {u.organization_name && <span className="ml-1.5 text-muted-foreground">— {u.organization_name}</span>}
              </li>
            ))}
        </ul>
      </ConfirmationModal>
    </PageLayout>
  );
};

export default UsersPage;
